import { currencyFilter } from "@/filters/currency.filter";
import {
  AccountManagement,
  CannasuitePlans
} from "@/interfaces/accountManagement";
import { store } from "@/internal";
import { AxiosResponse } from "axios";
import fromPairs from "lodash/fromPairs";
import keyBy from "lodash/keyBy";
import map from "lodash/map";
import snakeCase from "lodash/snakeCase";
import { Vue } from "vue-property-decorator";
import HttpService from "./http.service";
import { messagesService } from "./messages.service";

export class AccountService extends HttpService {
  protected uri = `${process.env.VUE_APP_BASE_URL}/accounts`;
  protected bckoUri = process.env.VUE_APP_BASE_URL_BACKOFFICE;

  public async getCreditCards(
    account: number,
    query?: object
  ): Promise<AccountManagement.CreditCard[]> {
    const response: AxiosResponse = await Vue.axios({
      method: "GET",
      url: [this.uri, account, "creditcards"].join("/"),
      params: query
    });
    const cards = response.data.data.data;
    return cards.map((card: any) => {
      return {
        id: card.id,
        nickname: card.nickname || `${card.name} - ${card.type}`,
        card_number: card.nro,
        card_type: card.type,
        cvv: card.cs,
        holder_name: card.name,
        expiration_month: card.expiration_month,
        expiration_year: card.expiration_year,
        identification_nro: card.identification_nro,
        identification_type: card.identification_type,
        address: card.address,
        city: card.city,
        country_id: card.country_id,
        state: card.state,
        postcode: card.postcode,
        default: card.default
      };
    }) as AccountManagement.CreditCard[];
  }
  public async getCreditCard(
    account: number,
    id: string,
    query?: object
  ): Promise<AccountManagement.CreditCard> {
    const response: AxiosResponse = await Vue.axios({
      method: "GET",
      url: [this.uri, account, "creditcards", id].join("/"),
      params: query
    });
    const responseCard = response.data.data;
    const card = {
      id: responseCard.id,
      nickname:
        responseCard.nickname || `${responseCard.name} - ${responseCard.type}`,
      card_number: responseCard.nro,
      card_type: responseCard.type,
      cvv: responseCard.cs,
      holder_name: responseCard.name,
      expiration_month: responseCard.expiration_month,
      expiration_year: responseCard.expiration_year,
      identification_nro: responseCard.identification_nro,
      identification_type: responseCard.identification_type,
      address: responseCard.address,
      city: responseCard.city,
      country_id: responseCard.country_id,
      state: responseCard.state,
      postcode: responseCard.postcode
    };
    return card as AccountManagement.CreditCard;
  }

  public async editCreditCard(
    account: number,
    id: number,
    card: Partial<AccountManagement.CreditCard>,
    query?: object
  ): Promise<AccountManagement.CreditCard> {
    const beCard: any = {
      name: card.holder_name,
      nro: card.card_number,
      cs: card.cvv,
      expiration_month: card.expiration_month,
      expiration_year: card.expiration_year,
      identification_nro: card.identification_nro,
      identification_type: card.identification_type,
      address: card.address,
      city: card.city,
      country_id: card.country_id,
      state: card.state,
      postcode: card.postcode
    };
    const response: AxiosResponse = await Vue.axios({
      method: "PUT",
      url: [this.uri, account, "creditcards", id].join("/"),
      data: beCard,
      params: query
    });
    return response.data as AccountManagement.CreditCard;
  }
  public async newCreditCard(
    account: number,
    card: AccountManagement.CreditCard,
    query?: object
  ): Promise<AccountManagement.CreditCard> {
    const beCard: any = {
      name: card.holder_name,
      nro: card.card_number,
      cs: card.cvv,
      account_id: account,
      expiration_month: card.expiration_month,
      expiration_year: card.expiration_year,
      identification_nro: card.identification_nro,
      identification_type: card.identification_type,
      address: card.address,
      city: card.city,
      country_id: card.country_id,
      state: card.state,
      postcode: card.postcode,
      type: card.card_type
    };
    const response: AxiosResponse = await Vue.axios({
      method: "POST",
      url: [this.uri, account, "creditcards"].join("/"),
      data: beCard,
      params: query
    });
    return response.data as AccountManagement.CreditCard;
  }

  public async deleteCreditCard(account: number, cardId: number) {
    try {
      const response: AxiosResponse = await Vue.axios({
        method: "DELETE",
        url: [this.uri, account, "creditcards", cardId].join("/")
      });
      messagesService.renderSuccessMessage("card_deleted_success");
      return response.data;
    } catch (e) {
      messagesService.renderErrorMessage(e);
    }
  }

  public async getAccount() {
    const response: AxiosResponse = await Vue.axios({
      url: `${this.bckoUri}/orders`,
      method: "GET"
    });
    // Reemplazar por la orden activa, hacer un find
    const activeOrder: AccountManagement.Account = response.data.data[0];
    return this.formatOrder(activeOrder);
  }

  public setAutoBill(orderId: number, autoBill: boolean) {
    return new Promise(async (resolve, reject) => {
      try {
        await Vue.axios({
          url: `${this.bckoUri}/orders/${orderId}/auto_bill`,
          method: "PUT",
          data: {
            auto_bill: +autoBill
          }
        });
        messagesService.renderSuccessMessage(
          "account_management_module.auto_bill_update"
        );
        resolve(true);
      } catch (e) {
        messagesService.renderErrorMessage(e);
        reject();
      }
    });
  }

  public async payInvoice(
    orderId: number,
    invoiceId: number,
    creditCardId: number
  ) {
    try {
      const response: AxiosResponse = await Vue.axios({
        url: `${this.bckoUri}/orders/${orderId}/invoice/${invoiceId}/charge`,
        method: "POST",
        data: {
          credit_card_id: creditCardId
        }
      });
      messagesService.renderSuccessMessage(
        "account_management_module.invoice_pay_ok"
      );
      return response.data;
    } catch (e) {
      messagesService.renderErrorMessage(e);
    }
  }

  public async getAvailability(accountId: number) {
    const response: AxiosResponse = await Vue.axios({
      url: `${this.bckoUri}/accounts/${accountId}/get_availability`,
      method: "GET"
    });

    return response.data;
  }

  public async starCreditCard(accountId: number, cardId: number) {
    try {
      const response: AxiosResponse = await Vue.axios({
        url: `/accounts/${accountId}/creditcards/${cardId}/star`,
        method: "POST"
      });
      messagesService.renderSuccessMessage(
        "account_management_module.auto_bill_setup_success"
      );
      return response.data.data;
    } catch (e) {
      messagesService.renderErrorMessage(e);
    }
  }

  public async downloadReceipt(invoiceId: number) {
    try {
      const order = store.getters["AccountManagementModule/accountData"];
      const response = await Vue.axios({
        url: `${this.bckoUri}/orders/${order.id}/invoice/${invoiceId}/pdf`,
        method: "GET",
        responseType: "blob"
      });
      const fileBlob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(fileBlob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `invoice${invoiceId}.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (e) {
      messagesService.renderErrorMessage(e);
    }
  }

  public async setExtraUsers(orderId: number, users: number) {
    try {
      const response: AxiosResponse = await Vue.axios({
        url: `${this.bckoUri}/orders/${orderId}/set_extra_users`,
        method: "POST",
        data: {
          extra_users: users
        }
      });
      messagesService.renderSuccessMessage(
        "account_management_module.extra_seats_successful"
      );
      return response.data.data;
    } catch (e) {
      messagesService.renderErrorMessage(e);
    }
  }

  public async setExtraLocations(orderId: number, locations: number) {
    try {
      const response: AxiosResponse = await Vue.axios({
        url: `${this.bckoUri}/orders/${orderId}/set_extra_locations`,
        method: "POST",
        data: {
          extra_locations: locations
        }
      });
      messagesService.renderSuccessMessage(
        "account_management_module.extra_seats_successful"
      );
      return response.data.data;
    } catch (e) {
      messagesService.renderErrorMessage(e);
    }
  }
  protected formatOrder(order: AccountManagement.Account) {
    order.outstanding_invoices = 0;
    order.invoices.forEach((invoice: AccountManagement.BillingInvoice) => {
      if (invoice.status === AccountManagement.BILL_STATUS.PENDING) {
        order.outstanding_invoices++;
      }
    });

    order.mainPlan = order.plans.find(
      (plan: CannasuitePlans.PlanDefinition) =>
        !!plan.products.find(
          product => product.type === "CANNASUITE_SEATS" && !!product.active
        )
    );

    return order;
  }

  /**
   *
   * @param planData a plan within a Invoices or Plans attributes
   */
  protected mapPlan(planData: any) {
    const parsedProducts = keyBy(
      map(planData.products, prod => {
        const settingsParsed = fromPairs(
          map(prod.settings.split(";"), value => {
            return value.split("=");
          })
        );
        return { ...prod, ...settingsParsed };
      }),
      product => snakeCase(product.name)
    );
    return {
      location_seats: parsedProducts.locations.max,
      cost_of_location_seats: currencyFilter(
        parsedProducts.locations.cost_per_unit
      ),
      available_location_seats: 0,
      user_seats: parsedProducts.seats.max,
      available_user_seats: 0,
      cost_of_user_seats: currencyFilter(parsedProducts.seats.cost_per_unit),
      apis: parsedProducts.api_integrations.max,
      used_apis: parsedProducts.api_integrations.used,
      cost_of_api_integration: currencyFilter(
        parsedProducts.api_integrations.cost_per_unit
      )
    };
  }
}

export default new AccountService();
